import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, required } from 'utils/validations'
import { UIButton } from 'components/ui/Button'
import { KeyboardEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { settingsActions, settingsSelectors } from 'store/settings'
import { messagesActions } from 'store/messages'
import { showSuccessToast } from 'utils/toast'
import { useModal } from 'providers/ModalProvider'
import { Col } from 'components/ui/Col'
import { Row } from 'components/ui/Row'
import { mainApiService } from 'api/config'
import { User } from 'types/user'

type Props = {
  onSuccess?: () => void
}

type ProfileForm = {
  name: string
  surname: string
}

export const ProfileModal = ({ onSuccess }: Props) => {
  const user = useSelector(settingsSelectors.userDetails)
  const { t } = useTranslation()
  const { close } = useModal()

  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()

  const onClickLogout = async () => {
    close()
    dispatch(messagesActions.setChatId(undefined))
    dispatch(messagesActions.setChats({}))
    dispatch(settingsActions.setTicketReminderVisible(false))
    dispatch(settingsActions.logout())
  }

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik<ProfileForm>({
    initialValues: {
      name: user?.name || '',
      surname: user?.surname || '',
    },
    validationSchema: createValidationSchema({
      name: required,
      surname: required,
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      console.debug({ values })
      try {
        setLoading(true)
        const updatedUser = await mainApiService.updateItem<User>('users', 'me', values)
        dispatch(settingsActions.setUserDetails(updatedUser))
        onSuccess?.()
        close()
        showSuccessToast(`${t('profileUpdatedSuccess')}`)
      } finally {
        setLoading(false)
      }
    },
  })

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <Col className={'tw-shadow-md tw-rounded-lg tw-w-96 tw-bg-primary-500 tw-p-4'}>
      <Row justifyContent={'space-between'} width={'100%'}>
        <div className={'tw-font-[800]'}>{t('profile')}</div>
        <div className={'tw-cursor-pointer'}>
          <FontAwesomeIcon icon={['far', 'times']} onClick={close} />
        </div>
      </Row>
      <Col width={'100%'} justifyContent={'flex-start'} alignItems={'flex-start'} className={'tw-mt-2 tw-gap-2'}>
        <Input label={t('name')} {...fieldProps('name')} onKeyDown={onKeyDown} className={'tw-w-full'} />
        <Input label={t('surname')} {...fieldProps('surname')} onKeyDown={onKeyDown} className={'tw-w-full'} />
        <div className={'tw-h-2'} />
        <Row className={'tw-text-sm tw-underline tw-cursor-pointer tw-gap-2'} onClick={onClickLogout}>
          {t('logout')}
          <FontAwesomeIcon icon={['far', 'sign-out']} onClick={close} />
        </Row>
      </Col>
      <div className={'tw-mt-6 tw-w-full'}>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={loading}>
          <span className={'tw-text-sm'}>{t('updateProfile')}</span>
        </UIButton>
      </div>
    </Col>
  )
}
